import decode from 'jwt-decode'
import history from '../routers/history'
import auth0 from 'auth0-js'
import { AUTH_CONFIG, META_DATA_PREFIX } from '../config/auth0'
import { LOCAL_STORAGE_AUTH_KEY, ROLES, LOCAL_STORAGE_POST_LOGIN_REDIRECT_URL, LOCAL_STORAGE_PROVIDERS_LIST_KEY, LOCAL_STORAGE_REPORT_TEMPLATES_KEY, LOCAL_STORAGE_POST_LOGIN_REDIRECT_SEARCH } from '../config/constants'
import { REFRESH_TOKEN_WAIT_TIME } from '../config/api-endpoints'
import { userGroupSettings } from '../config/user-group-settings'
import store from 'store'
import { routesConfig } from '../routers/constants'
import * as Sentry from '@sentry/browser';
import { errorNotification } from '../utils/notification-helpers'
import { resendEmailApiCall } from '../apis/servicesApi'
import { message } from 'antd'

export default class Auth {
  constructor() {
    this.auth0 = new auth0.WebAuth(AUTH_CONFIG);

    this.login = this.login.bind(this);
    this.logout = this.logout.bind(this);
    this.loggedIn = this.loggedIn.bind(this);
    this.getProfile = this.getProfile.bind(this);
    this.getLoginsCount = this.getLoginsCount.bind(this);
    this.getUserProfile = this.getUserProfile.bind(this);
    this.getNewTokensForAPI = this.getNewTokensForAPI.bind(this);
    this.makeCallToCheckSession = this.makeCallToCheckSession.bind(this);
    this.handleAuthentication = this.handleAuthentication.bind(this);
    this.isAuthenticated = this.isAuthenticated.bind(this);
    this.resendConfirmation = this.resendConfirmation.bind(this);
    this.signUp = this.signUp.bind(this);
    this.updateUserEmailVerificationInLocalStorage =
      this.updateUserEmailVerificationInLocalStorage.bind(this);
  }

  signUp(payload, callback) {
    payload.connection = "AHPatients";
    this.auth0.signup(payload, callback);
  }

  resendConfirmation() {
    const params = {};
    this.auth0.sendEmailVerification(params, (err) => {
      if (err) {
        // Handle error.
        console.log(err);
      }
      console.log("email sent");
    });
  }

  async makeCallToCheckSession() {
    return new Promise((resolve, reject) =>
      this.auth0.checkSession(
        {
          audience: AUTH_CONFIG.audience,
          scope: AUTH_CONFIG.scope,
        },
        (error, result, response) => {
          if (error) {
            reject(error);
          } else {
            resolve(result, response);
          }
        }
      )
    );
  }

  async getNewTokensForAPI() {
    try {
      const response = await this.makeCallToCheckSession().then((result) => {
        const { accessToken, idToken } = result;
        store.set(LOCAL_STORAGE_AUTH_KEY.ACCESS_TOKEN, accessToken);
        store.set(LOCAL_STORAGE_AUTH_KEY.ID_TOKEN, idToken);
        return accessToken;
      });
      return response;
    } catch (e) {
      return e;
    }
  }

  login(username, password, callback) {
    this.auth0.login(
      {
        realm: "TeleRadUsers",
        username,
        password,
      },
      callback
    );
  }

  loggedIn() {
    let expiresAt = store.get(LOCAL_STORAGE_AUTH_KEY.EXPIRES_AT);
    return new Date().getTime() < expiresAt;
  }

  facilityId() {
    let facilityId = store.get(
      LOCAL_STORAGE_AUTH_KEY.SELECTED_FACILITY_ID
    );
    return facilityId
  }

  handleAuthentication() {
    this.auth0.parseHash((err, authResult) => {
      if (authResult && authResult.accessToken && authResult.idToken) {
        this.setSession(authResult);
      } else if (err) {
        history.replace("/login");
        console.log(err);
        message.info("You don’t have permission to access FrontDesk. Check with your administrator for access.")
      }
    });
  }

  updateUserSignatureInLocalStorage(newUserProfile) {
    const newSignatureUrl = newUserProfile["user_metadata"]["signature"];
    let currentProfileInStorage = store.get(
      LOCAL_STORAGE_AUTH_KEY.USER_PROFILE
    );
    currentProfileInStorage[META_DATA_PREFIX + "user_meta_data"]["signature"] =
      newSignatureUrl;
    store.set(LOCAL_STORAGE_AUTH_KEY.USER_PROFILE, currentProfileInStorage);
  }

  updateUserAppMetadataInLocalStorage(appMetadata) {
    let currentProfileInStorage = store.get(
      LOCAL_STORAGE_AUTH_KEY.USER_PROFILE
    );
    currentProfileInStorage[META_DATA_PREFIX + "app_meta_data"] = appMetadata;
    store.set(LOCAL_STORAGE_AUTH_KEY.USER_PROFILE, currentProfileInStorage);
  }

  updateUserEmailVerificationInLocalStorage() {
    let currentProfileInStorage = store.get(
      LOCAL_STORAGE_AUTH_KEY.USER_PROFILE
    );
    currentProfileInStorage["email_verified"] = true;
    store.set(LOCAL_STORAGE_AUTH_KEY.USER_PROFILE, currentProfileInStorage);
  }

  setSession(authResult, skipRedirect = false) {
    // Set the time that the access token will expire at
    let expiresAt = JSON.stringify(
      authResult.expiresIn * 1000 + new Date().getTime()
    );
    store.set(LOCAL_STORAGE_AUTH_KEY.ACCESS_TOKEN, authResult.accessToken);
    store.set(LOCAL_STORAGE_AUTH_KEY.ID_TOKEN, authResult.idToken);
    store.set(LOCAL_STORAGE_AUTH_KEY.EXPIRES_AT, expiresAt);
    this.auth0.client.userInfo(authResult.accessToken, async (err, user) => {
      if (!err) {
        if (user) {
          Sentry.configureScope(function (scope) {
            scope.setTag("userEmail", user.email);
          });
        }
        store.set(LOCAL_STORAGE_AUTH_KEY.USER_PROFILE, user);

        if (!user.email_verified) {
          resendEmailApiCall(user?.sub).then((res)=>{
            if(res?.responseStatus >= 200 && res?.responseStatus < 300){
              message.info(('Please verify your email.'))
            } else if(res?.responseStatus === 401){
              message.info(('Please verify your email.'))
              throw Error(res?.responseStatus);
            } else {
              errorNotification(('Please verify your email.'))
            }
          })
          store.remove(LOCAL_STORAGE_AUTH_KEY.USER_PROFILE)
          store.remove(LOCAL_STORAGE_AUTH_KEY.ACCESS_TOKEN)
          store.remove(LOCAL_STORAGE_AUTH_KEY.ID_TOKEN)
          store.remove(LOCAL_STORAGE_AUTH_KEY.EXPIRES_AT)
          store.remove(LOCAL_STORAGE_PROVIDERS_LIST_KEY)
          store.remove(LOCAL_STORAGE_REPORT_TEMPLATES_KEY)
          store.clearAll()
          history.replace(routesConfig.LOGIN,user)
        } else{
          if (!skipRedirect) {
            const postLoginRedirectUrl = store.get(LOCAL_STORAGE_POST_LOGIN_REDIRECT_URL)
            const postLoginRedirectSearch = store.get(LOCAL_STORAGE_POST_LOGIN_REDIRECT_SEARCH)
            store.remove(LOCAL_STORAGE_POST_LOGIN_REDIRECT_SEARCH)
            store.remove(LOCAL_STORAGE_POST_LOGIN_REDIRECT_URL)
            history.replace({ pathname: postLoginRedirectUrl || '/', search: postLoginRedirectSearch || "" })
          }
        }
      }
    });
  }

  logout() {
    // Clear access token and ID token from local storage
    // logger.logUserSignOut()
    store.remove(LOCAL_STORAGE_AUTH_KEY.USER_PROFILE);
    store.remove(LOCAL_STORAGE_AUTH_KEY.ACCESS_TOKEN);
    store.remove(LOCAL_STORAGE_AUTH_KEY.ID_TOKEN);
    store.remove(LOCAL_STORAGE_AUTH_KEY.EXPIRES_AT);
    store.remove(LOCAL_STORAGE_PROVIDERS_LIST_KEY);
    store.remove(LOCAL_STORAGE_REPORT_TEMPLATES_KEY);
    store.clearAll();
    // navigate to the home route
    history.push("/login");
  }

  getProfile() {
    const userProfile = this.getUserProfile();
    if (!userProfile) {
      console.log("Having problem fetching user profile. Try Refresh the page");
      const profile = {
        userBasicInfo: {},
        userSettings: {},
      };
      return profile;
    }
    const { email, name, nickname, picture, sub } = userProfile;
    const appMetaData =
      userProfile && userProfile[META_DATA_PREFIX + "app_meta_data"];
    const userMetaData =
      userProfile && userProfile[META_DATA_PREFIX + "user_meta_data"];
    let homeFacility,
      signature,
      specialty,
      userType,
      main,
      admin,
      associatedGroups,
      approvedFacilities,
      affiliatedGroup,
      userPhone,
      userTitle,
      userCredentials,
      completedConnectOnboardingIds,
      country;
    if (userMetaData) {
      country= userMetaData["country"] || "";
      homeFacility = userMetaData["home_facility"] || "";
      signature = userMetaData["signature"] || "";
      specialty = userMetaData["specialty"] || "";
      userType = userMetaData["user_type"] || "";
      userPhone = userMetaData["phone_number"] || "";
      userTitle = userMetaData["title"] || "";
      userCredentials = userMetaData["credentials"] || "";
    }
    if (appMetaData) {
      main = appMetaData["main"] === "1";
      admin = appMetaData["admin"] === "1";
      associatedGroups = appMetaData["associated_groups"];
      approvedFacilities = appMetaData["approved_facilities"];
      affiliatedGroup = appMetaData["affiliated_group"];
      completedConnectOnboardingIds =
        appMetaData["completed_connect_onboarding_ids"];
    }
    const profile = {
      userBasicInfo: {
        country,
        avatarSrc: picture,
        email,
        userPhone,
        homeFacility,
        userRealName: name,
        signature,
        specialty,
        userGuid: sub,
        userName: nickname,
        userTitle,
        userCredentials,
      },
      userSettings: {
        isAdmin: admin,
        isMain: main,
        isIndependentRadiologist: (associatedGroups && associatedGroups.length > 0) || userType === ROLES.INDEPENDENT_RADIOLOGIST,
        affiliatedGroup, // group guid
        approvedFacilities,
        associatedGroups,
        userRole: "radiologist", // currentUserGroup : 'radiologist'
        userType, // currentUserRole: userType -> radiographer, coordinator, radiologist, independent radiologist
        completedConnectOnboardingIds,
      },
    };
    return profile;
  }

  getApprovedFacility() {
    const userProfile = this.getUserProfile();
    if (!userProfile) {
      console.log("Having problem fetching user profile. Try Refresh the page");
      const profile = {
        userBasicInfo: {},
        userSettings: {},
      };
      return profile;
    }
    const appMetaData = userProfile && userProfile[META_DATA_PREFIX + "app_meta_data"];
    return appMetaData?.approved_facilities;
  }

  getUserProfile() {
    const profileValueFromStorage = store.get(
      LOCAL_STORAGE_AUTH_KEY.USER_PROFILE
    );
    let profile;
    if (profileValueFromStorage !== "undefined") {
      profile = profileValueFromStorage;
    }
    return profile;
  }
  getProfileRequestBodyForUpdate() {
    const userProfile = this.getUserProfile();
    if (!userProfile) {
      return null;
    }
    const {
      email,
      email_verified: emailVerified,
      name,
      nickname,
      picture,
      sub,
    } = userProfile;
    const appMetaData =
      userProfile && userProfile[META_DATA_PREFIX + "app_meta_data"];
    const userMetaData =
      userProfile && userProfile[META_DATA_PREFIX + "user_meta_data"]
    return {
      email,
      email_verified: emailVerified,
      name,
      nickname,
      picture,
      sub,
      user_metadata: userMetaData,
      app_metadata: appMetaData,
    };
  }

  isNotificationAllowed() {
    return false;
    // const profile = this.getProfile()
    // if (profile) {
    //   const { userSettings: { userType } } = profile
    //   return userType !== 'radiographer'
    // }
    // return false
  }

  getUserGroupSettings() {
    const profile = this.getProfile();
    if (!profile) {
      return {
        permissions: {
          allowedSidebarItems: ["works", "studies"],
        },
      };
    }
    const {
      userSettings: {
        isAdmin,
        isMain,
        isIndependentRadiologist,
        approvedFacilities,
      },
    } = profile;

    const radiologistSettings = userGroupSettings();
    let settings = {
      ...radiologistSettings,
      permissions: {
        allowedSidebarItems: ["works"],
        allowedToAddStudy: true, // !isIndependentRadiologist,
        allowedToAddIndependentStudy: true, // isIndependentRadiologist,
        assignServiceProvider: true,
        editDetail: true,
        editReport: true,
        viewDetail: true,
        viewReport: true,
      },
    };
    if (isAdmin || isMain) {
      settings.permissions.allowedSidebarItems.push('my-uploads')
      settings.permissions.allowedSidebarItems.push('studies')
      if(isAdmin){
       settings.permissions.allowedSidebarItems.push('admin')
      }
    } else if (approvedFacilities && approvedFacilities.length > 0) {
      settings.permissions.allowedSidebarItems.push("my-uploads");
      settings.permissions.allowedSidebarItems.push("studies");
    } else if (isIndependentRadiologist) {
      settings.permissions.allowedSidebarItems.push("my-uploads");
    }
    settings.permissions.allowedSidebarItems.push("shared-studies");
    settings.permissions.allowedSidebarItems.push("settings");
    return settings;
  }

  getPatientName() {
    const profileValueFromStorage = store.get(
      LOCAL_STORAGE_AUTH_KEY.USER_PROFILE
    );
    let profile;
    if (profileValueFromStorage !== "undefined") {
      profile = profileValueFromStorage;
    }
    return profile;
  }

  getToken() {
    return store.get(LOCAL_STORAGE_AUTH_KEY.ID_TOKEN);
  }
  setToken(idToken) {
    store.set(LOCAL_STORAGE_AUTH_KEY.ID_TOKEN, idToken);
  }
  refreshToken() {
    this.auth0.checkSession({}, (err, authResult) => {
      if (authResult && authResult.accessToken && authResult.idToken) {
        this.setSession(authResult, true);
      } else if (err) {
        history.replace("/login");
        console.log(err);
        window.alert(
          `Error: ${err.error}. Check the console for further details.`
        );
      }
    });
  }
  isTokenNearingExpiration() {
    try {
      const token = this.getToken();
      const decoded = decode(token);
      const currentTimeInSeconds = Date.now() / 1000;
      const difference = decoded.exp - currentTimeInSeconds;
      return difference < REFRESH_TOKEN_WAIT_TIME;
    } catch (err) {
      console.error(err);
      return false;
    }
  }
  isTokenExpired(token) {
    try {
      const decoded = decode(token);
      return decoded.exp < Date.now() / 1000;
    } catch (err) {
      console.error(err);
      return true;
    }
  }

  getLoginsCount() {
    const userProfile = this.getUserProfile();
    if (!userProfile) {
      return null;
    }
    const loginCounts =
      userProfile && userProfile[META_DATA_PREFIX + "logins_count"];
    return loginCounts;
  }

  isAuthenticated() {
    // Check whether the current time is past the
    // access token's expiry time
    let expiresAt = store.get(LOCAL_STORAGE_AUTH_KEY.EXPIRES_AT);
    return new Date().getTime() < expiresAt;
  }
}
